import { CheckOutlined, CopyOutlined } from "@ant-design/icons"
import { Tooltip, Typography } from "antd"
import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { empty } from "../constants/placeholders"
import { cn } from "../utils/cn"

/**
 * The GC code leading a uid is identical for every record in a tenant, so in a
 * table it is the same four characters repeated down the column. `short` drops
 * it from the display only — the full reference stays in the tooltip and is
 * what the copy action puts on the clipboard.
 */
const SHORT_UID_RE = /^[0-9A-Z]{4}([LP][0-9A-Z]{5})$/

const shortenUid = (uid: string): string => SHORT_UID_RE.exec(uid)?.[1] ?? uid

interface RecordRefProps {
  uid: string | null | undefined
  type?: string | null
  city?: string | null
  state?: string | null
  customerName?: string | null
  compact?: boolean
  short?: boolean
  className?: string
}

export const RecordRef = ({
  uid,
  type,
  city,
  state,
  customerName,
  compact = false,
  short = false,
  className
}: RecordRefProps) => {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current)
    }
  }, [])

  if (!uid) {
    return <Typography.Text className={className}>{empty}</Typography.Text>
  }

  // The UID is one unbroken run, no separators (PRD 4.10). Stripping them
  // here rather than trusting the payload keeps the rule from breaking during
  // the window where a deployed frontend meets an older backend.
  const displayUid = uid.replace(/[\s-]/g, "").toUpperCase()
  const shownUid = short ? shortenUid(displayUid) : displayUid
  const cityState = city ? [city, state].filter(Boolean).join(", ") : null
  const segments = compact ? [customerName] : [type, cityState, customerName]
  const details = segments.filter(Boolean).join(" · ")

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    await navigator.clipboard.writeText(displayUid)
    setCopied(true)
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current)
    copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div
      className={cn(
        "group inline-flex items-center gap-1 max-w-full",
        className
      )}
      data-testid="record-ref"
    >
      <Tooltip title={shownUid === displayUid ? undefined : displayUid}>
        <Typography.Text
          className="shrink-0 whitespace-nowrap font-mono font-semibold tracking-wide"
          data-testid="record-ref-uid"
        >
          {shownUid}
        </Typography.Text>
      </Tooltip>
      {details && (
        <Typography.Text
          className="min-w-0"
          data-testid="record-ref-details"
          ellipsis
          type="secondary"
        >
          {` · ${details}`}
        </Typography.Text>
      )}
      <Tooltip
        title={copied ? t("recordRef.copied") : t("recordRef.copy")}
        open={copied ? true : undefined}
      >
        <button
          type="button"
          data-testid="record-ref-copy"
          aria-label={copied ? t("recordRef.copied") : t("recordRef.copy")}
          onClick={handleCopy}
          onDoubleClick={(e) => e.stopPropagation()}
          className="block shrink-0 cursor-pointer text-sm text-gray-400 transition-opacity hover:text-primary md:pointer-events-none md:opacity-0 md:group-hover:pointer-events-auto md:group-hover:opacity-100 md:focus-visible:pointer-events-auto md:focus-visible:opacity-100"
        >
          {copied ? (
            <CheckOutlined className="text-success" />
          ) : (
            <CopyOutlined />
          )}
        </button>
      </Tooltip>
    </div>
  )
}
